import { DealChecklistType, DocAssociation, FIMenuSection, LanguageCode, PersonType } from '@core/classes/SharedEnums';
import { BitFlagHelper } from '@core/helpers/enum-helper';
import FIMenuSectionEnumsHelper from '@core/_enums/FIMenuSectionEnumsHelper';
import { IChecklistFieldToMatchConfig } from '@core/classes/Checklist/ChecklistFieldToMatch';
import { SaturnPermissions } from '@core/classes/AccessEmployeeClass';
import util from '@core/services/util';

export default class SettingsChecklistItem {
	/** ElasticSearch ID of this checklist config item */
	id: string;

	/** When true, indicates that this checklist item was created for a specific deal by an FI Manager */
	isCustomItem: boolean;

	/** A list of associations that should be generated for this checklist item. */
	associationInfo: IChecklistAssociation[];

	/** A display name for this checklist item, shown to FI Managers */
	name: string;

	/** A brief description for this checklist item, shown to FI Managers */
	description: string;

	/**
	 * A list of display information, meant to be shown to customers. Will always at least have an English translation available. Only applicable if
	 * the `sendTo` list includes any customers.
	 */
	customerDisplay: IChecklistDisplay[];

	/**
	 * The type of deal participants who should receive this checklist item. If empty, it's assumed that the deal employee is responsible for completing
	 * this checklist item.
	 */
	sendTo: PersonType[];

	/** Private version of the `type` property. */
	private _type: DealChecklistType;

	/** The type of checklist item this is. This will determine pre-defined behavior for how this checklist item can be resolved. */
	get type(): DealChecklistType {
		return this._type;
	}

	set type(newType: DealChecklistType) {
		this._type = newType;

		// Making sure all relevant fields get reset
		const isDocAction = this.isDocumentAction();
		const isPlaidAction = this.isPlaidAction();

		if (isDocAction) this.subtype = null;
		else this.resetVerifyPermissions();

		if (!isDocAction && !isPlaidAction) {
			this.associationInfo = [];
		}
	}

	/** An optional subtype that may further describe this checklist item. This is specific to the `type`, and not all checklist types have available subtypes. */
	subtype?: string;

	/** When a checklist item has a section dependency, the deal cannot progress past that section until all required manual verifications are submitted. */
	sectionDependencies: IChecklistSectionDependency[];

	/**
	 * An expression that returns a list of any object. When this expression is provided, allows this checklist item to be self-duplicating, meaning that a
	 * separate checklist item will be generated for each item of the list this expression returns.
	 */
	getListExpression?: string;

	/** C# code to be run to determine whether or not this checklist item is required. Should be the body of a method that returns a boolean value. */
	isRequiredExpression: string;

	/** C# code to be run to determine whether or not this checklist item has been successfully cleared. Should be the body of a method that returns a
	 * `ChecklistVerification` object (see `ChecklistItem.cs`). Some checklist types have auto-verification available and may not need a custom
	 * `isVerified` expression.
	 */
	isVerifiedExpression?: string;

	/** Only applicable if this checklist item requires manual verification. When manually verifying the results of a checklist item, will pull data fields from FIMenu to
	 * display alongside the checklist results, allowing for easier comparison.
	 */
	fieldsToMatch?: IChecklistFieldToMatchConfig[];

	/** Some checklist types require pre-defined additional fields. Refer to `ChecklistMetadata.cs` for a full list of all metadata objects. */
	metadata?: { [key: string]: unknown };

	constructor(init?: Partial<SettingsChecklistItem>) {
		Object.assign(
			this,
			{
				id: crypto.randomUUID(),
				customerDisplay: [],
				isCustomItem: false,
				associationInfo: [],
				sendTo: [],
				sectionDependencies: [],
				getListExpression: SettingsChecklistItem.getDefaultGetList(),
				isRequiredExpression: SettingsChecklistItem.getDefaultIsRequired(),
				isVerifiedExpression: SettingsChecklistItem.getDefaultIsVerified(),
				fieldsToMatch: [],
			},
			init,
		);

		this.sectionDependencies.sort((a, b) => FIMenuSectionEnumsHelper.getOrder(a.section) - FIMenuSectionEnumsHelper.getOrder(b.section));
	}

	/** Will reset all relevant fields to their default values. These default values can be overwritten by passing in an `init` object. */
	resetItem(init?: Partial<SettingsChecklistItem>) {
		this.id = init?.id ?? crypto.randomUUID();
		this.name = init?.name ?? null;
		this.description = init?.description ?? null;
		this.customerDisplay = init?.customerDisplay ?? [{ name: null, description: null, language: LanguageCode.English }];
		this._type = init?.type ?? null;
		this.subtype = init?.subtype ?? null;
		this.isCustomItem = init?.isCustomItem ?? false;
		this.associationInfo = init?.associationInfo ?? [];
		this.sendTo = init?.sendTo ?? [];
		this.sectionDependencies = init?.sectionDependencies ?? [];
		this.isRequiredExpression = init?.isRequiredExpression ?? null;
		this.isVerifiedExpression = init?.isVerifiedExpression ?? null;
		this.fieldsToMatch = init?.fieldsToMatch ?? [];
		this.metadata = init?.metadata ?? undefined;
	}

	resetVerifyPermissions() {
		this.sectionDependencies = [];
	}

	addNewSectionDependency(section: FIMenuSection) {
		this.sectionDependencies.push({
			section: section,
			optionalVerificationBy: SaturnPermissions.None,
			requiredVerificationBy: SaturnPermissions.None,
		});

		this.sectionDependencies.sort((a, b) => FIMenuSectionEnumsHelper.getOrder(a.section) - FIMenuSectionEnumsHelper.getOrder(b.section));
	}

	setSectionVerifications(section: FIMenuSection, permissions: SaturnPermissions, isRequired: boolean) {
		const sectionToEdit = this.sectionDependencies.find(s => s.section === section);
		if (!sectionToEdit) throw new Error('Section is not inside of current section dependency list.');

		if (isRequired) sectionToEdit.requiredVerificationBy = permissions;
		else sectionToEdit.optionalVerificationBy = permissions;
	}

	removeSectionDependency(section: FIMenuSection) {
		this.sectionDependencies = this.sectionDependencies.filter(s => s.section !== section);
	}

	getPermissionsWhoCanVerify(): SaturnPermissions {
		const combinedPermissions = SaturnPermissions.None;

		this.sectionDependencies.forEach(s => {
			BitFlagHelper.addFlag(combinedPermissions, s.optionalVerificationBy);
			BitFlagHelper.addFlag(combinedPermissions, s.requiredVerificationBy);
		});

		return combinedPermissions;
	}

	hasSectionDependency(section: FIMenuSection): boolean {
		return this.sectionDependencies.some(s => s.section === section);
	}

	requiresManualVerification(): boolean {
		return this.sectionDependencies.some(s => s.requiredVerificationBy !== SaturnPermissions.None);
	}

	isPlaidAction(): boolean {
		return SettingsChecklistItem.isPlaidAction(this.type);
	}

	isDocumentAction(): boolean {
		return SettingsChecklistItem.isDocumentAction(this.type);
	}

	static getDefaultGetList(): string {
		return (
			'/* Create a list of objects that will be fed into other expressions for this item. */\n' +
			'/* When the checklist gets generated, a separate checklist item will be created for each item in this list. */\n' +
			'return new List<object>();'
		);
	}

	static getDefaultIsVerified(): string {
		return '/* When are conditions for this item fulfilled? */ \n/* What items were used to check verification? */ \nreturn new ChecklistSimpleVerification();';
	}

	static getDefaultIsRequired(): string {
		return '/* When is this item needed? */ \nreturn false;';
	}

	/** Returns true if this item's `type` involves launching a Plaid Link session. */
	static isPlaidAction(checklistType: DealChecklistType): boolean {
		if (checklistType == null) return false;

		const keyName = util.getKeyFromValue(DealChecklistType, checklistType) as keyof typeof DealChecklistType;
		return keyName && keyName.startsWith('LaunchPlaid');
	}

	/** Returns true if this item's `type` involves some kind of document upload. */
	static isDocumentAction(checklistType: DealChecklistType): boolean {
		if (checklistType == null) return false;

		const keyName = util.getKeyFromValue(DealChecklistType, checklistType) as keyof typeof DealChecklistType;
		return keyName && keyName.startsWith('Upload');
	}
}

export interface IChecklistAssociation {
	/** The type of association to generate */
	type: DocAssociation;

	/** When provided, will retrieve the ID(s) to be used for this association. When not provided, association will be attempted to be auto-mapped from the data model. */
	getIdExpression?: string;
}

export interface IChecklistDisplay {
	name: string;
	description: string;
	language: LanguageCode;
}

export interface IChecklistSectionDependency {
	section: FIMenuSection;
	optionalVerificationBy: SaturnPermissions;
	requiredVerificationBy: SaturnPermissions;
}
